import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useSpring } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Maximize2, X, ArrowUpRight } from 'lucide-react';

import PageHero from '@/components/layout/PageHero';
import MetricCountUp from '@/components/brand/MetricCountUp';
import CtaBand from '@/components/sections/CtaBand';
import { viewportOnce } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useCaseStudyDetail } from '@/hooks/useCaseStudyDetail';
import { useCaseStudiesData } from '@/hooks/useCaseStudiesData';

const EASE = [0.22, 1, 0.36, 1];

const SECTION_DEFS = [
  { id: 'overview', label: 'Overview' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'tech', label: 'Tech Stack' },
  { id: 'challenge', label: 'Challenge' },
  { id: 'solution', label: 'Solution' },
  { id: 'results', label: 'Results' },
];

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, loading, notFound } = useCaseStudyDetail(slug);
  const { data: listData } = useCaseStudiesData();
  const reducedMotion = useReducedMotion();

  // Update page meta from the case study
  useEffect(() => {
    if (!data?.meta) return;
    if (data.meta.title) document.title = data.meta.title;
    const setMeta = (sel, attr, name, content) => {
      if (!content) return;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('meta[name="description"]', 'name', 'description', data.meta.description);
    setMeta('meta[property="og:title"]', 'property', 'og:title', data.meta.title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', data.meta.description);
    if (data.meta.og_image) {
      const abs = /^https?:\/\//i.test(data.meta.og_image)
        ? data.meta.og_image
        : `${window.location.origin}${data.meta.og_image.startsWith('/') ? '' : '/'}${data.meta.og_image}`;
      setMeta('meta[property="og:image"]', 'property', 'og:image', abs);
    }
  }, [data?.meta]);

  // Measure the real navbar height at runtime so the sticky progress track sits
  // flush below it regardless of the navbar's actual size. Also track the
  // rendered progress-track height so anchor jumps land below both.
  const trackRef = useRef(null);
  const [navH, setNavH] = useState(160);
  const [trackH, setTrackH] = useState(52);

  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('header');
      if (header) setNavH(header.offsetHeight || 160);
      if (trackRef.current) setTrackH(trackRef.current.offsetHeight || 52);
    };
    measure();
    // ResizeObserver covers nav logo loading + responsive breakpoint changes
    const ro = new ResizeObserver(measure);
    const header = document.querySelector('header');
    if (header) ro.observe(header);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  if (notFound) {
    return (
      <>
        <PageHero title="Case Study Not Found" subtitle="The case study you're looking for doesn't exist." crumb="Case Study" />
        <section className="py-16">
          <div className="container mx-auto text-center">
            <Link to="/case-studies" data-testid="cs-back-link" className="text-sm font-semibold text-brandblue hover:text-[#C9962E]">
              ← Back to all case studies
            </Link>
          </div>
        </section>
        <CtaBand />
      </>
    );
  }

  if (loading || !data) {
    return (
      <section className="py-32 bg-sky-50">
        <div className="container mx-auto text-center text-sm text-slatebody">Loading case study…</div>
      </section>
    );
  }

  // Normalise raw DB fields → canonical shape used throughout this page
  const cs = (() => {
    const raw = data.case_study;
    const tech_stack = (() => {
      const src = raw.tech_stack || raw.tech || [];
      return Array.isArray(src) ? src.map((t) => (typeof t === 'string' ? { name: t } : t)) : [];
    })();
    return {
      ...raw,
      tech_stack,
      metric: raw.metric || raw.badge || '',
      brand: raw.brand || raw.client || '',
      read_time: raw.read_time || raw.readTime || '',
      img: raw.img || raw.image || '',
    };
  })();
  const prev = data.prev_slug;
  const next = data.next_slug;

  // Year parsed from the case study date (e.g. "June 3, 2023" → "2023")
  const csYear = (() => {
    const m = (cs.date || '').match(/\b(20\d{2})\b/);
    return m ? m[1] : '';
  })();

  // Spec-sheet facts for the overview — de-duplicated so identical
  // industry/category values never render twice.
  const overviewFacts = [
    { label: 'Client', value: cs.brand || cs.client },
    { label: 'Industry', value: cs.industry },
    { label: 'Category', value: cs.category },
    { label: 'Year', value: csYear },
  ]
    .filter((f) => f.value)
    .filter((f, idx, arr) => arr.findIndex((x) => x.value === f.value) === idx)
    .slice(0, 4);

  // Filter visible sections to those with content
  const visibleSections = SECTION_DEFS.filter((s) => {
    if (s.id === 'overview') return true;
    if (s.id === 'gallery') return Array.isArray(cs.gallery) && cs.gallery.length;
    if (s.id === 'tech') return Array.isArray(cs.tech_stack) && cs.tech_stack.length;
    if (s.id === 'challenge') return Array.isArray(cs.challenges) && cs.challenges.length;
    if (s.id === 'solution') return Array.isArray(cs.solution) && cs.solution.length;
    if (s.id === 'results') return !!cs.results;
    return false;
  });

  // Related = other visible case studies, max 3
  const related = (listData?.collections?.caseStudies || [])
    .filter((c) => c.slug && c.slug !== slug)
    .slice(0, 3);

  // 16px breathing room below the sticky track when an anchor jump lands
  const scrollOffset = navH + trackH + 16;
  const sectionStyle = { scrollMarginTop: `${scrollOffset}px` };

  return (
    <>
      <EditorialHero cs={cs} prev={prev} next={next} navigate={navigate} reducedMotion={reducedMotion} navH={navH} />

      <LeftProgressRail sections={visibleSections} navH={navH} trackH={trackH} trackRef={trackRef} />

      {/* ─── Overview — the brief + client's vision (structured two-tone) ─ */}
      <section id="overview" style={sectionStyle} className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto max-w-6xl">
          <SectionEyebrow eyebrow="Overview" title="The client's vision" />
          <div className="mt-12 grid lg:grid-cols-[1.35fr_1fr] gap-6 lg:gap-7 items-stretch">
            {/* The brief + facts */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportOnce}
              transition={{ duration: 0.6, ease: EASE }}
              data-testid="cs-overview-brief"
              className="relative rounded-3xl border border-hairline bg-white p-8 sm:p-10 shadow-[0_14px_40px_rgba(31,45,69,0.07)] overflow-hidden"
            >
              <span
                aria-hidden="true"
                className="absolute -top-px left-10 right-10 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#C9962E] to-transparent"
              />
              <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-brandblue">The brief</p>
              <p className="mt-5 text-lg sm:text-xl text-navy-deep/90 leading-relaxed">
                {cs.description ||
                  `${cs.title} — delivered by MGS for ${cs.brand || cs.client || 'a partner brand'}.`}
              </p>
              {overviewFacts.length > 0 && (
                <dl className="mt-9 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-hairline pt-7">
                  {overviewFacts.map((f) => (
                    <OverviewFact key={f.label} label={f.label} value={f.value} />
                  ))}
                </dl>
              )}
            </motion.div>

            {/* Client's vision — navy quote panel (clean, no decorative glyph) */}
            {cs.clients_vision && (
              <motion.figure
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportOnce}
                transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
                data-testid="cs-clients-vision"
                className="relative flex flex-col rounded-3xl bg-navy-deep text-white p-8 sm:p-10 overflow-hidden"
              >
                <span
                  aria-hidden="true"
                  className="absolute -right-16 -top-16 h-56 w-56 rounded-full pointer-events-none"
                  style={{ background: 'radial-gradient(circle, rgba(232,194,90,0.22), transparent 65%)' }}
                />
                <p className="relative text-[11px] font-bold tracking-[0.22em] uppercase text-[#E8C25A]">
                  In their words
                </p>
                <blockquote className="relative mt-5 flex-1">
                  <p className="text-xl sm:text-2xl font-medium tracking-tight leading-relaxed text-white">
                    {cs.clients_vision}
                  </p>
                </blockquote>
                <figcaption className="relative mt-8 pt-6 border-t border-white/10">
                  <p className="text-sm font-semibold text-white">{cs.brand || cs.client || 'Client'}</p>
                  {(cs.industry || cs.category) && (
                    <p className="mt-1 text-[11px] font-bold tracking-[0.2em] uppercase text-white/55">
                      {cs.industry || cs.category}
                    </p>
                  )}
                </figcaption>
              </motion.figure>
            )}
          </div>
        </div>
      </section>

      {/* ─── At a glance metrics ─────────────────────────────────────────── */}
      <AtAGlance cs={cs} />

      {/* ─── Gallery (App Store style snap scroll) ───────────────────────── */}
      {Array.isArray(cs.gallery) && cs.gallery.length > 0 && (
        <section id="gallery" style={sectionStyle} className="py-16 sm:py-20 bg-white">
          <div className="container mx-auto">
            <SectionEyebrow eyebrow="Gallery" title="Project Gallery" subtitle="A closer look at the build. Click any screen to zoom in." />
          </div>
          <div className="mt-12">
            <CsGallery images={cs.gallery} title={cs.title} />
          </div>
        </section>
      )}

      {/* ─── Technology Stack — premium animated tiles ───────────────────── */}
      {Array.isArray(cs.tech_stack) && cs.tech_stack.length > 0 && (
        <section id="tech" style={sectionStyle} className="relative py-16 sm:py-20 bg-sky-50 overflow-hidden">
          {/* Slow-drifting gradient backdrop for depth */}
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
            <motion.span
              className="absolute -top-24 -left-24 h-80 w-80 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(47,111,176,0.16), transparent 65%)' }}
              animate={reducedMotion ? {} : { x: [0, 30, 0], y: [0, 20, 0] }}
              transition={reducedMotion ? {} : { duration: 14, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.span
              className="absolute -bottom-24 -right-16 h-80 w-80 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(232,194,90,0.18), transparent 65%)' }}
              animate={reducedMotion ? {} : { x: [0, -28, 0], y: [0, -18, 0] }}
              transition={reducedMotion ? {} : { duration: 16, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>

          <div className="relative container mx-auto">
            <SectionEyebrow eyebrow="Tech Stack" title="Technology Stack" subtitle="The battle-tested tools we used to ship this work." />
            <div className="mt-14 flex flex-wrap items-stretch justify-center gap-5 sm:gap-6" data-testid="cs-tech-stack">
              {cs.tech_stack.map((t, i) => (
                <motion.div
                  key={t.name + i}
                  initial={{ opacity: 0, y: 24, scale: 0.96 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={viewportOnce}
                  transition={{ duration: 0.55, delay: i * 0.07, ease: EASE }}
                  whileHover={{ y: -8 }}
                  className="will-change-transform"
                >
                  <motion.div
                    animate={reducedMotion ? {} : { y: [0, -7, 0] }}
                    transition={reducedMotion ? {} : { duration: 4.5 + (i % 4) * 0.4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.25 }}
                    className="group relative h-full w-[150px] sm:w-[160px] rounded-2xl bg-white border border-hairline p-6 text-center shadow-[0_10px_28px_rgba(31,45,69,0.07)] hover:shadow-[0_24px_50px_rgba(31,45,69,0.16)] hover:border-[#E8C25A]/50 transition-all duration-300 overflow-hidden"
                    title={t.name}
                  >
                    {/* Gold top accent reveals on hover */}
                    <span
                      aria-hidden="true"
                      className="absolute -top-px left-6 right-6 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#E8C25A] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    />
                    {/* Soft glow on hover */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{ background: 'radial-gradient(circle at 50% 0%, rgba(232,194,90,0.16), transparent 70%)' }}
                    />
                    {/* Icon disc */}
                    <div className="relative mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-[#2BA8E0]/15 to-[#E8C25A]/25 ring-1 ring-hairline flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                      {t.icon_url ? (
                        <img
                          src={t.icon_url}
                          alt={t.name}
                          loading="lazy"
                          className="h-9 w-9 object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                        />
                      ) : (
                        <span
                          className="text-2xl font-bold text-transparent bg-clip-text"
                          style={{ backgroundImage: 'linear-gradient(135deg, #1F4373 0%, #2F6FB0 50%, #C9962E 100%)' }}
                        >
                          {(t.name || '?').trim().charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <p className="relative mt-4 text-sm font-semibold tracking-tight text-navy-deep leading-snug">{t.name}</p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Challenges (sticky left, scrolling right) ──────────────────── */}
      {Array.isArray(cs.challenges) && cs.challenges.length > 0 && (
        <ChallengeSolutionBlock id="challenge" testId="cs-challenges" eyebrow="The Challenges" heading="Challenges" subtitle="What we needed to solve before we could ship." items={cs.challenges} accent sectionStyle={sectionStyle} />
      )}

      {/* ─── Solution ────────────────────────────────────────────────────── */}
      {Array.isArray(cs.solution) && cs.solution.length > 0 && (
        <ChallengeSolutionBlock id="solution" testId="cs-solution" eyebrow="The Solution" heading="Solution" subtitle="How we built it." items={cs.solution} sectionStyle={sectionStyle} />
      )}

      {/* ─── Results — distinct full-width navy panel with stat cards ──── */}
      {cs.results && (
        <ResultsPanel cs={cs} sectionStyle={sectionStyle} />
      )}

      {/* ─── Related case studies ───────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="py-16 sm:py-20 bg-white">
          <div className="container mx-auto">
            <SectionEyebrow eyebrow="More Work" title="More Case Studies" />
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cs-related">
              {related.map((r, i) => (
                <motion.div
                  key={r.slug}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewportOnce}
                  transition={{ duration: 0.55, delay: i * 0.07, ease: EASE }}
                >
                  <Link
                    to={`/case-studies/${r.slug}`}
                    data-testid={`cs-related-${i}`}
                    className="group block rounded-2xl overflow-hidden bg-white border border-hairline shadow-[0_10px_28px_rgba(31,45,69,0.08)] hover:shadow-[0_22px_50px_rgba(31,45,69,0.16)] hover:-translate-y-1.5 transition-all duration-300"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-sky-100">
                      {(r.image || r.img) ? (
                        <img src={r.image || r.img} alt={r.title} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[900ms] group-hover:scale-[1.06] will-change-transform" />
                      ) : (
                        <div aria-hidden="true" className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #E6F1FB 0%, #C9DCEF 55%, #E8C25A20 100%)' }} />
                      )}
                      <span className="absolute right-4 top-4 h-9 w-9 rounded-full bg-white/90 backdrop-blur inline-flex items-center justify-center text-navy-deep opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all">
                        <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                    <div className="p-6">
                      <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-brandblue">{r.category || 'Case Study'}</p>
                      <h4 className="mt-2.5 text-[15px] font-semibold tracking-tight text-navy-deep leading-snug line-clamp-2 group-hover:text-[#C9962E] transition-colors">{r.title}</h4>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {(!data || data?.sections?.ctaBand) && <CtaBand data={data?.sections?.ctaBand?.data} />}
    </>
  );
}

/* ─── Editorial stacked hero (no full-bleed image) ──────────────────────── */

function EditorialHero({ cs, prev, next, navigate, reducedMotion, navH = 160 }) {
  // Parse year from cs.date if present (e.g. "September 14, 2023" → "2023")
  const year = (() => {
    const m = (cs.date || '').match(/\b(20\d{2})\b/);
    return m ? m[1] : '';
  })();
  // cs fields are already normalised by the parent — just alias for clarity
  const servicesLabel = cs.tech_stack.length
    ? `${cs.tech_stack.length} Technologies`
    : 'Engineering · Product';

  // Split metric into number + label fragment for the giant outcome display
  const metricParts = (() => {
    const m = (cs.metric || '').match(/([+−\-]?\d[\d.,]*\s*[%×x+]?)\s*(.*)/);
    if (!m) return null;
    return { value: m[1], rest: (m[2] || '').trim() };
  })();

  return (
    <section
      data-testid="cs-hero"
      style={{ paddingTop: `${navH + 40}px` }}
      className="relative bg-white border-b border-hairline pb-12 sm:pb-14 overflow-hidden"
    >
      {/* Soft gradient accent backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(900px 480px at 12% 18%, rgba(47,111,176,0.10), transparent 65%), radial-gradient(720px 400px at 88% 90%, rgba(232,194,90,0.10), transparent 70%)',
        }}
      />
      <div className="relative container mx-auto">
        <Link
          to="/case-studies"
          data-testid="cs-back-link"
          className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase font-bold text-slatebody hover:text-navy-deep transition-colors mb-10"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Case Studies
        </Link>

        <div className="text-center max-w-4xl mx-auto">
          {/* Thin meta bar */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-[10.5px] font-bold tracking-[0.24em] uppercase text-slatebody/80"
          >
            <MetaPill>{cs.brand || cs.client || 'Client'}</MetaPill>
            <Dot />
            <MetaPill>{cs.category || 'Industry'}</MetaPill>
            {year && (
              <>
                <Dot />
                <MetaPill>{year}</MetaPill>
              </>
            )}
            <Dot />
            <MetaPill>{servicesLabel}</MetaPill>
          </motion.div>

          {/* Oversized display headline */}
          <div className="mt-7 overflow-hidden">
            <motion.h1
              data-testid="cs-detail-title"
              initial={reducedMotion ? { y: 0, opacity: 1 } : { y: '110%', opacity: 0.001 }}
              animate={{ y: '0%', opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
              className="text-3xl sm:text-5xl lg:text-[3.4rem] xl:text-[3.6rem] font-bold tracking-[-0.015em] text-navy-deep leading-[1.08] will-change-transform"
            >
              {cs.title}
            </motion.h1>
          </div>

          {/* Byline */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.35, ease: EASE }}
            className="mt-5 text-[12px] tracking-[0.22em] uppercase font-bold text-slatebody/70"
          >
            {cs.date && <span>{cs.date}</span>}
            {cs.date && (cs.read_time || cs.readTime) && <span className="mx-3 text-slatebody/40">/</span>}
            {(cs.read_time || cs.readTime) && <span>{cs.read_time || cs.readTime}</span>}
          </motion.p>

          {/* Centered outcome metric */}
          {metricParts && (
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
              data-testid="cs-hero-outcome"
              className="mt-10"
            >
              <p className="text-[10px] font-bold tracking-[0.24em] uppercase text-[#C9962E] mb-2">Outcome</p>
              <div className="flex items-end justify-center gap-5 flex-wrap">
                <span
                  className="block text-[3.5rem] sm:text-[5rem] lg:text-[5.5rem] font-bold tracking-[-0.03em] leading-[0.9] text-transparent bg-clip-text"
                  style={{ backgroundImage: 'linear-gradient(135deg, #1F4373 0%, #2F6FB0 50%, #C9962E 100%)' }}
                >
                  <MetricCountUp value={metricParts.value} />
                </span>
                {metricParts.rest && (
                  <p className="pb-2 text-sm sm:text-base font-semibold text-navy-deep/80 leading-snug max-w-[18ch] text-left">
                    {metricParts.rest}
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* Prev / next — centered under outcome */}
          <div className="mt-10 hidden sm:flex items-center justify-center gap-3">
            <MagneticNavButton
              testId="cs-prev"
              label="Previous case study"
              disabled={!prev}
              onClick={() => prev && navigate(`/case-studies/${prev}`)}
              reducedMotion={reducedMotion}
              dark
            >
              <ChevronLeft className="h-5 w-5" />
            </MagneticNavButton>
            <MagneticNavButton
              testId="cs-next"
              label="Next case study"
              disabled={!next}
              onClick={() => next && navigate(`/case-studies/${next}`)}
              reducedMotion={reducedMotion}
              dark
            >
              <ChevronRight className="h-5 w-5" />
            </MagneticNavButton>
          </div>
        </div>

        {/* Inline framed editorial image (supporting, not dominating) */}
        {(cs.img || cs.image) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-14 rounded-2xl overflow-hidden border border-hairline shadow-[0_24px_60px_rgba(15,25,45,0.14)] max-w-[1100px] mx-auto"
          >
            <div className="aspect-[21/9] bg-navy-deep">
              <img
                src={cs.img || cs.image}
                alt={cs.title}
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}

/* ─── Reusable centered section eyebrow + title + gold divider ─────────── */

function SectionEyebrow({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-2xl mx-auto">
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-brandblue"
      >
        {eyebrow}
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.05, ease: EASE }}
        className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy-deep leading-[1.05]"
      >
        {title}
      </motion.h2>
      <motion.span
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, amount: 0 }}
        transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
        style={{ transformOrigin: 'center' }}
        className="mx-auto mt-5 block h-[2px] w-16 rounded-full bg-gradient-to-r from-[#2BA8E0] via-[#2F6FB0] to-[#E8C25A]"
      />
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0 }}
          transition={{ duration: 0.55, delay: 0.2, ease: EASE }}
          className="mt-5 text-base sm:text-lg text-slatebody leading-relaxed"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}

function MetaPill({ children }) {
  return <span className="text-slatebody/80">{children}</span>;
}
function Dot() {
  return <span aria-hidden="true" className="h-1 w-1 rounded-full bg-slatebody/35" />;
}

function OverviewFact({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[10px] font-bold tracking-[0.2em] uppercase text-slatebody/60">{label}</dt>
      <dd className="mt-1.5 text-sm font-semibold text-navy-deep leading-snug">{value}</dd>
    </div>
  );
}

/* ─── Magnetic nav button (reuses brand magnetic logic) ────────────────── */

function MagneticNavButton({ children, testId, label, onClick, disabled, reducedMotion, dark = false }) {
  const btnRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 220, damping: 18, mass: 0.5 });
  const y = useSpring(my, { stiffness: 220, damping: 18, mass: 0.5 });

  const onMove = (e) => {
    if (disabled || reducedMotion) return;
    const r = btnRef.current?.getBoundingClientRect?.();
    if (!r) return;
    const px = e.clientX - (r.left + r.width / 2);
    const py = e.clientY - (r.top + r.height / 2);
    mx.set(Math.max(-7, Math.min(7, px * 0.22)));
    my.set(Math.max(-5, Math.min(5, py * 0.22)));
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <motion.button
      ref={btnRef}
      style={{ x, y }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileTap={disabled ? {} : { scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 360, damping: 22 }}
      data-testid={testId}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={
        dark
          ? 'h-12 w-12 rounded-full bg-sky-50 border border-hairline text-navy-deep inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-navy-deep hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/70'
          : 'h-12 w-12 rounded-full bg-white/15 backdrop-blur text-white inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white hover:text-navy-deep transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8C25A]/70'
      }
    >
      {children}
    </motion.button>
  );
}

/* ─── Sticky TOP centered progress track (editorial signature) ──────────── */

function LeftProgressRail({ sections, navH = 160, trackH = 52, trackRef }) {
  // Despite the legacy name kept for testids, this is now a top horizontal
  // centered progress track — looks balanced and doesn't compete with content.
  const [active, setActive] = useState(sections[0]?.id);
  const [scrollPct, setScrollPct] = useState(0);

  useEffect(() => {
    const observers = sections.map((s) => {
      const el = document.getElementById(s.id);
      if (!el) return null;
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActive(s.id);
        },
        { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
      );
      io.observe(el);
      return io;
    });
    return () => observers.forEach((io) => io && io.disconnect());
  }, [sections]);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      setScrollPct(max > 0 ? doc.scrollTop / max : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - (navH + trackH + 16);
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <nav
      ref={trackRef}
      aria-label="Section navigation"
      data-testid="cs-scrollspy"
      style={{ top: `${navH}px` }}
      className="hidden md:block sticky z-30 pointer-events-none mb-2"
    >
      <div className="container mx-auto flex justify-center">
        <div className="pointer-events-auto relative inline-flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/90 backdrop-blur-md ring-1 ring-hairline shadow-[0_10px_28px_rgba(15,25,45,0.08)] overflow-hidden">
          {/* Scroll-progress fill running behind the labels */}
          <span
            aria-hidden="true"
            className="absolute inset-y-0 left-0 transition-[width] duration-200 ease-out"
            style={{
              width: `${Math.round(scrollPct * 100)}%`,
              background:
                'linear-gradient(90deg, rgba(232,194,90,0.18) 0%, rgba(201,150,46,0.18) 60%, transparent 100%)',
            }}
          />
          {sections.map((s) => {
            const isActive = active === s.id;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => goTo(e, s.id)}
                data-testid={`cs-spy-${s.id}`}
                className="relative isolate inline-flex items-center px-3 sm:px-4 py-1.5 rounded-full text-[10.5px] sm:text-[11px] tracking-[0.18em] uppercase font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/60"
              >
                {isActive && (
                  <motion.span
                    layoutId="cs-spy-pill"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    className="absolute inset-0 rounded-full bg-navy-deep shadow-[0_6px_16px_rgba(15,25,45,0.25)] -z-10"
                  />
                )}
                <span className={isActive ? 'text-white' : 'text-navy/60 hover:text-navy-deep'}>{s.label}</span>
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

/* ─── At-a-glance metrics ───────────────────────────────────────────────── */

function AtAGlance({ cs }) {
  // Derive 3-4 stats from the case study (metric + counts of items)
  const stats = useMemo(() => {
    const items = [];
    if (cs.metric) items.push({ value: cs.metric, label: 'Outcome' });
    if (cs.tech_stack?.length) items.push({ value: `${cs.tech_stack.length}+`, label: 'Technologies' });
    if (cs.challenges?.length) items.push({ value: `${cs.challenges.length}`, label: 'Key Challenges' });
    if (cs.solution?.length) items.push({ value: `${cs.solution.length}`, label: 'Solution Pillars' });
    return items.slice(0, 4);
  }, [cs]);

  if (!stats.length) return null;
  return (
    <section className="py-12 sm:py-16 bg-sky-50/40 border-y border-hairline" data-testid="cs-at-a-glance">
      <div className="container mx-auto">
        <SectionEyebrow eyebrow="At a glance" title="The project in numbers" />
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div
              key={s.label + i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportOnce}
              transition={{ duration: 0.55, delay: i * 0.06, ease: EASE }}
              className="text-center"
            >
              <div className="text-4xl sm:text-5xl font-bold tracking-tight text-navy-deep leading-none">
                <MetricCountUp value={s.value} />
              </div>
              <p className="mt-3 text-[11px] font-bold tracking-[0.22em] uppercase text-slatebody/80">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Challenge / Solution block (centered header + numbered cards) ────── */

function ChallengeSolutionBlock({ id, testId, eyebrow, heading, subtitle, items, accent = false, sectionStyle }) {
  return (
    <section
      id={id}
      data-testid={testId}
      style={sectionStyle}
      className={`py-16 sm:py-20 ${accent ? 'bg-sky-50' : 'bg-white'}`}
    >
      <div className="container mx-auto">
        <SectionEyebrow eyebrow={eyebrow} title={heading || eyebrow.replace(/^The\s+/i, '')} subtitle={subtitle} />
        <div className="mt-12 relative max-w-3xl mx-auto">
          {/* Vertical connector line behind the numbers */}
          <div
            aria-hidden="true"
            className="absolute left-[19px] top-4 bottom-4 w-px"
            style={{ background: 'linear-gradient(to bottom, #C9962E22 0%, #1F437333 50%, #C9962E11 100%)' }}
          />
          <ul className="space-y-8">
            {items.map((it, i) => (
              <motion.li
                key={(it.title || '') + i}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportOnce}
                transition={{ duration: 0.6, delay: i * 0.07, ease: EASE }}
                className="relative flex gap-6 pl-0"
              >
                <span className={`relative z-10 shrink-0 h-10 w-10 rounded-full bg-navy-deep text-white inline-flex items-center justify-center text-[12px] font-bold tracking-[0.06em] ring-4 ${accent ? 'ring-sky-50' : 'ring-white'}`}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 pb-2">
                  <h4 className="text-xl font-bold tracking-tight text-navy-deep leading-snug">{it.title}</h4>
                  <p className="mt-2.5 text-slatebody text-base sm:text-lg leading-relaxed">{it.desc}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ─── Results — distinct deep-navy full-width panel + count-up stat cards ─ */

function ResultsPanel({ cs, sectionStyle }) {
  // Derive 3 outcome stats from the case study
  const stats = useMemo(() => {
    const items = [];
    if (cs.metric) items.push({ value: cs.metric, label: 'Headline Outcome' });
    if (cs.tech_stack?.length) items.push({ value: `${cs.tech_stack.length}+`, label: 'Technologies Shipped' });
    if (cs.solution?.length) items.push({ value: `${cs.solution.length}`, label: 'Solution Pillars' });
    if (cs.challenges?.length) items.push({ value: `${cs.challenges.length}`, label: 'Key Challenges' });
    return items.slice(0, 3);
  }, [cs]);

  return (
    <section
      id="results"
      data-testid="cs-results"
      className="relative py-16 sm:py-20 overflow-hidden"
      style={{ ...(sectionStyle || {}), background: 'radial-gradient(900px 600px at 80% 10%, rgba(232,194,90,0.18), transparent 60%), linear-gradient(180deg, #0F192D 0%, #1F4373 100%)' }}
    >
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-40 h-[460px] w-[460px] rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(47,111,176,0.35), transparent 65%)' }}
      />
      <div className="relative container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.65, ease: EASE }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#E8C25A]">Results</p>
          <h3 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05] drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
            Outcomes that matter
          </h3>
          <span
            aria-hidden="true"
            className="mx-auto mt-5 block h-[2px] w-16 rounded-full bg-gradient-to-r from-[#C9962E] to-[#E8C25A]"
          />
          <p className="mt-6 text-white/90 text-base sm:text-lg leading-relaxed">
            {cs.results}
          </p>
        </motion.div>

        {stats.length > 0 && (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {stats.map((s, i) => (
              <motion.div
                key={s.label + i}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportOnce}
                transition={{ duration: 0.55, delay: i * 0.08, ease: EASE }}
                className="relative rounded-2xl bg-white/8 backdrop-blur-md ring-1 ring-white/15 p-8 overflow-hidden text-center"
              >
                <span
                  aria-hidden="true"
                  className="absolute -top-px left-6 right-6 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#E8C25A] to-transparent"
                />
                <div className="text-5xl sm:text-6xl font-bold tracking-[-0.02em] leading-none text-transparent bg-clip-text"
                  style={{ backgroundImage: 'linear-gradient(135deg, #FFFFFF 0%, #E8C25A 100%)' }}
                >
                  <MetricCountUp value={s.value} />
                </div>
                <p className="mt-5 text-[11px] font-bold tracking-[0.22em] uppercase text-white/75">{s.label}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Gallery — arrow carousel + dots + click-to-zoom lightbox ──────────── */

function CsGallery({ images, title }) {
  const reducedMotion = useReducedMotion();
  const scrollerRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(-1);
  const [paused, setPaused] = useState(false);
  const urls = images.map((g) => (typeof g === 'string' ? g : g?.url || ''));
  const count = urls.length;

  // Width of one slide (card + gap), measured from the rendered DOM so it stays
  // correct across breakpoints and container padding.
  const stepSize = () => {
    const el = scrollerRef.current;
    if (!el) return 1;
    const slides = el.querySelectorAll('[data-slide]');
    if (slides.length >= 2) {
      return slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
    }
    return el.clientWidth || 1;
  };

  const goTo = (i, smooth = true) => {
    const el = scrollerRef.current;
    if (!el) return;
    const ni = ((i % count) + count) % count;
    el.scrollTo({ left: stepSize() * ni, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
    setIndex(ni);
  };

  // Keep the active index in sync with manual / native-swipe scrolling.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(el.scrollLeft / stepSize());
        setIndex(Math.max(0, Math.min(count - 1, i)));
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [count]);

  // Gentle auto-advance — pauses on hover/focus and while the lightbox is open.
  useEffect(() => {
    if (reducedMotion || paused || lightbox !== -1 || count <= 1) return undefined;
    const id = setInterval(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const step = stepSize();
      const ni = (Math.round(el.scrollLeft / step) + 1) % count;
      el.scrollTo({ left: step * ni, behavior: 'smooth' });
      setIndex(ni);
    }, 4200);
    return () => clearInterval(id);
  }, [reducedMotion, paused, lightbox, count]);

  return (
    <div data-testid="cs-gallery">
      <div
        ref={scrollerRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="overflow-x-auto no-scrollbar pb-2"
      >
        <div className="container mx-auto flex gap-5 sm:gap-7">
          {urls.map((src, i) => (
            <motion.figure
              key={src + i}
              data-slide=""
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
              className="shrink-0 w-[80vw] sm:w-[520px] lg:w-[660px]"
            >
              <button
                type="button"
                onClick={() => setLightbox(i)}
                aria-label={`View ${title} screen ${i + 1} larger`}
                className="group relative block w-full rounded-2xl overflow-hidden ring-1 ring-hairline shadow-[0_14px_38px_rgba(31,45,69,0.10)] cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E] focus-visible:ring-offset-2"
              >
                <span className="absolute left-4 top-4 z-10 inline-flex items-center justify-center h-8 px-3 rounded-full bg-white/85 backdrop-blur text-[11px] font-bold tracking-[0.18em] text-navy-deep">
                  {String(i + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                </span>
                <span className="absolute right-4 top-4 z-10 h-9 w-9 rounded-full bg-white/90 backdrop-blur inline-flex items-center justify-center text-navy-deep opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
                  <Maximize2 className="h-4 w-4" />
                </span>
                <div className="relative aspect-[16/10] overflow-hidden bg-navy-deep">
                  <img
                    src={src}
                    alt={`${title} screenshot ${i + 1}`}
                    loading="lazy"
                    draggable={false}
                    className="w-full h-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-[1.04] will-change-transform"
                  />
                </div>
              </button>
            </motion.figure>
          ))}
          {/* Trailing spacer so the last card can scroll fully into view */}
          <div className="shrink-0 w-4" aria-hidden="true" />
        </div>
      </div>

      {count > 1 && (
        <div className="container mx-auto mt-9 flex items-center justify-center gap-4" data-testid="cs-gallery-controls">
          <CsGalleryArrow dir="left" onClick={() => goTo(index - 1)} label="Previous screen" />
          <div className="flex items-center gap-2.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to screen ${i + 1}`}
                aria-current={i === index}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  i === index ? 'w-7 bg-gradient-to-r from-[#C9962E] to-[#E8C25A]' : 'w-2 bg-hairline2 hover:bg-slatebody/40'
                )}
              />
            ))}
          </div>
          <CsGalleryArrow dir="right" onClick={() => goTo(index + 1)} label="Next screen" />
        </div>
      )}

      <CsLightbox images={images} index={lightbox} title={title} onClose={() => setLightbox(-1)} onIndex={setLightbox} />
    </div>
  );
}

function CsGalleryArrow({ dir, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="h-11 w-11 rounded-full bg-white border border-hairline inline-flex items-center justify-center text-navy-deep shadow-[0_6px_18px_rgba(31,45,69,0.08)] hover:bg-navy-deep hover:text-white hover:border-navy-deep transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E] focus-visible:ring-offset-2"
    >
      {dir === 'left' ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
    </button>
  );
}

function CsLightbox({ images, index, title, onClose, onIndex }) {
  const open = index >= 0;
  const urls = images.map((g) => (typeof g === 'string' ? g : g?.url || ''));
  const count = urls.length;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex((index + 1) % count);
      else if (e.key === 'ArrowLeft') onIndex((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, index, count, onClose, onIndex]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid="cs-gallery-lightbox"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-deep/90 backdrop-blur-sm p-4 sm:p-10"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {count > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + count) % count); }}
              aria-label="Previous screen"
              className="absolute left-3 sm:left-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.28, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-6xl"
          >
            <img
              src={urls[index]}
              alt={`${title} screen ${index + 1}`}
              className="w-full max-h-[82vh] object-contain rounded-xl shadow-[0_40px_90px_rgba(0,0,0,0.6)]"
            />
            <figcaption className="mt-4 text-center text-white/70 text-[12px] tracking-[0.18em] uppercase font-bold">
              {title} — {String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
            </figcaption>
          </motion.figure>

          {count > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % count); }}
              aria-label="Next screen"
              className="absolute right-3 sm:right-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
